import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  Briefcase, 
  Plus, 
  Search, 
  Filter, 
  Users, 
  Calendar,
  Edit,
  Trash2,
  Eye,
  MoreVertical,
  TrendingUp,
  Clock,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Tag,
  X,
  ChevronDown,
  Save,
  MessageSquare,
  FileText,
  UserPlus
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { consultantsService, ConsultantService } from '../../services/consultants.service';
import { trainingsService } from '../../services/trainings.service';
import ApprovalStatusBadge from '../../components/common/ApprovalStatusBadge';
import { API_ENDPOINTS } from '../../config/api';
import { Crown } from 'lucide-react';

const ServicesPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedDomain, setSelectedDomain] = useState('all');
  const [selectedFormat, setSelectedFormat] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [services, setServices] = useState<ConsultantService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [canCreate, setCanCreate] = useState<{ canCreate: boolean; message?: string } | null>(null);
  const [checkingPermission, setCheckingPermission] = useState(false);
  const [showLimitMessage, setShowLimitMessage] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    completed: 0,
    totalRevenue: 0,
    averageRating: 0
  });
  const [notification, setNotification] = useState<{
    type: 'approved' | 'rejected' | 'pending';
    serviceTitle: string;
    rejectionReason?: string;
    isVisible: boolean;
  }>({
    type: 'pending',
    serviceTitle: '',
    isVisible: false
  });

  // Domain options for consultant services
  const domainOptions = [
    { id: 1, name: 'Affaires Réglementaires Générales' },
    { id: 2, name: 'Bonnes Pratiques de Fabrication (BPF)' },
    { id: 3, name: 'Bonnes Pratiques Cliniques (BPC)' },
    { id: 4, name: 'Bonnes Pratiques de Laboratoire (BPL)' },
    { id: 5, name: 'Pharmacovigilance' },
    { id: 6, name: 'Audit Qualité et Conformité' },
    { id: 7, name: 'Systèmes de Management de la Qualité (SMQ)' },
    { id: 8, name: 'Validation et Qualification' },
    { id: 9, name: 'Développement Pharmaceutique' },
    { id: 10, name: 'Recherche et Développement (R&D)' },
    { id: 11, name: 'Formulation et Galénique' },
    { id: 12, name: 'Chimie Analytique Pharmaceutique' },
    { id: 13, name: 'Biotechnologie Pharmaceutique' },
    { id: 14, name: 'Production et Fabrication' },
    { id: 15, name: 'Opérations de Fabrication Stérile' },
    { id: 16, name: 'Contrôle Qualité de Production' },
    { id: 17, name: 'Ingénierie et Maintenance Pharmaceutique' },
    { id: 18, name: 'Chaîne d\'Approvisionnement Pharmaceutique' },
    { id: 19, name: 'Technologies de Fabrication Avancées' },
    { id: 20, name: 'Affaires Commerciales et Marketing' },
    { id: 21, name: 'Marketing Pharmaceutique' },
    { id: 22, name: 'Vente Pharmaceutique' },
    { id: 23, name: 'Accès au Marché' },
    { id: 24, name: 'Économie de la Santé' },
    { id: 25, name: 'Management et Leadership' },
    { id: 26, name: 'Gestion de Projet Pharmaceutique' },
    { id: 27, name: 'Leadership et Gestion d\'Équipe' },
    { id: 28, name: 'Stratégie et Innovation' },
    { id: 29, name: 'Sécurité et Environnement' },
    { id: 30, name: 'Santé, Sécurité, Environnement (SSE)' },
    { id: 31, name: 'Biosécurité' },
    { id: 32, name: 'Autres Domaines Spécialisés' },
    { id: 33, name: 'Dispositifs Médicaux' },
    { id: 34, name: 'Cosmétiques' },
    { id: 35, name: 'Nutrition et Diététique' }
  ];

  const formatOptions = [
    { value: 'presentiel', label: t('consultant.services.filters.format.presentiel', 'In-person') },
    { value: 'distance', label: t('consultant.services.filters.format.distance', 'Remote') },
    { value: 'hybride', label: t('consultant.services.filters.format.hybride', 'Hybrid') }
  ];

  useEffect(() => {
    fetchServices();
    fetchStats();
  }, []);

  const checkCreatePermission = async () => {
    try {
      setCheckingPermission(true);
      const result = await trainingsService.canCreateTraining();
      setCanCreate(result);
    } catch (error) {
      setCanCreate({ canCreate: false, message: 'Erreur lors de la vérification des permissions' });
    } finally {
      setCheckingPermission(false);
    }
  };

  const fetchServices = async () => {
    try {
      setLoading(true);
      const data = await consultantsService.getMyServices();
      setServices(data.services);
      setError(null);
    } catch (err) {
      setError(t('consultant.services.error.load', 'Error loading services'));
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await consultantsService.getMyServices();
      const total = data.total;
      const active = data.services.filter(s => s.is_active).length;
      const completed = data.services.filter(s => s.status === 'approved').length;
      
      setStats({
        total,
        active,
        completed,
        totalRevenue: 0, // TODO: Calculate from quotes/revenue
        averageRating: 0 // TODO: Calculate from reviews
      });
    } catch (err) {
    }
  };

  const handleDeleteService = async (id: string) => {
    if (window.confirm(t('consultant.services.delete.confirm', 'Are you sure you want to delete this service?'))) {
      try {
        await consultantsService.deleteService(id);
        await fetchServices(); // Refresh the list
        alert(t('consultant.services.delete.success', 'Service deleted successfully'));
      } catch (err) {
        alert(t('consultant.services.delete.error', 'Error deleting service'));
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'text-green-600 bg-green-50';
      case 'pending':
        return 'text-yellow-600 bg-yellow-50';
      case 'refused':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'approved':
        return t('common.status.approved', 'Approved');
      case 'pending':
        return t('common.status.pending', 'Pending');
      case 'refused':
        return t('common.status.rejected', 'Rejected');
      default:
        return t('common.status.unknown', 'Unknown');
    }
  };

  const filteredServices = services.filter(service => {
    const matchesSearch = service.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = selectedStatus === 'all' || service.status === selectedStatus;
    const matchesDomain = selectedDomain === 'all' || service.domaine_id === parseInt(selectedDomain);
    const matchesFormat = selectedFormat === 'all' || service.format === selectedFormat;
    
    return matchesSearch && matchesStatus && matchesDomain && matchesFormat;
  });

  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedStatus('all');
    setSelectedDomain('all');
    setSelectedFormat('all');
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (selectedDomain !== 'all') count++;
    if (selectedFormat !== 'all') count++;
    return count;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('consultant.services.header.title', 'My Services')}</h1>
          <p className="text-gray-600">{t('consultant.services.header.subtitle', 'Manage your consulting services')}</p>
        </div>
        <button
          onClick={async () => {
            if (canCreate === null || checkingPermission) {
              await checkCreatePermission();
            }
            if (canCreate && canCreate.canCreate) {
              navigate('/consultant/services/create');
            } else {
              setShowLimitMessage(true);
            }
          }}
          disabled={checkingPermission}
          className="inline-flex items-center px-4 py-2 bg-brand-primary text-white font-medium rounded-lg hover:bg-brand-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {checkingPermission ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {t('consultant.services.button.checking', 'Vérification...')}
            </>
          ) : (
            <>
              <Plus className="h-4 w-4 mr-2" />
              {t('consultant.services.button.new', 'New service')}
            </>
          )}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-md border border-gray-100 p-4 hover:shadow-lg transition-all duration-300 group"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">{t('consultant.services.stats.total', 'Total services')}</p>
              <p className="text-xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="w-10 h-10 bg-gradient-to-br from-brand-primary to-brand-primary/80 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <Briefcase className="h-5 w-5 text-white" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-md border border-gray-100 p-4 hover:shadow-lg transition-all duration-300 group"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">{t('consultant.services.stats.active', 'Active services')}</p>
              <p className="text-xl font-bold text-gray-900">{stats.active}</p>
            </div>
            <div className="w-10 h-10 bg-gradient-to-br from-brand-secondary to-brand-secondary/80 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <Users className="h-5 w-5 text-white" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-md border border-gray-100 p-4 hover:shadow-lg transition-all duration-300 group"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">{t('consultant.services.stats.approved', 'Approved services')}</p>
              <p className="text-xl font-bold text-gray-900">{stats.completed}</p>
            </div>
            <div className="w-10 h-10 bg-gradient-to-br from-brand-mint to-brand-mint/80 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <TrendingUp className="h-5 w-5 text-white" />
            </div>
          </div>
        </motion.div>

        {/* <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-md border border-gray-100 p-4 hover:shadow-lg transition-all duration-300 group"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Demandes Clients</p>
              <p className="text-xl font-bold text-gray-900">0</p>
            </div>
            <div className="w-10 h-10 bg-gradient-to-br from-brand-accent to-brand-accent/80 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <MessageSquare className="h-5 w-5 text-white" />
            </div>
          </div>
        </motion.div> */}
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder={t('consultant.services.search.placeholder', 'Search a service...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
              />
            </div>
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Filter className="h-4 w-4 mr-2" />
            {t('consultant.services.filters.toggle', 'Filters')}
            {getActiveFiltersCount() > 0 && (
              <span className="ml-2 px-2 py-1 bg-brand-primary text-white text-xs rounded-full">
                {getActiveFiltersCount()}
              </span>
            )}
            <ChevronDown className={`h-4 w-4 ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 pt-4 border-t border-gray-200"
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('consultant.services.filters.status.label', 'Status')}</label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                >
                  <option value="all">{t('consultant.services.filters.status.all', 'All statuses')}</option>
                  <option value="approved">{t('common.status.approved', 'Approved')}</option>
                  <option value="pending">{t('common.status.pending', 'Pending')}</option>
                  <option value="refused">{t('common.status.rejected', 'Rejected')}</option>
                </select>
              </div>

              {/* Domain Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('consultant.services.filters.domain.label', 'Domain')}</label>
                <select
                  value={selectedDomain}
                  onChange={(e) => setSelectedDomain(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                >
                  <option value="all">{t('consultant.services.filters.domain.all', 'All domains')}</option>
                  {domainOptions.map((domain) => (
                    <option key={domain.id} value={domain.id}>
                      {domain.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Format Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('consultant.services.filters.format.label', 'Format')}</label>
                <select
                  value={selectedFormat}
                  onChange={(e) => setSelectedFormat(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                >
                  <option value="all">{t('consultant.services.filters.format.all', 'All formats')}</option>
                  {formatOptions.map((format) => (
                    <option key={format.value} value={format.value}>
                      {format.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Clear Filters */}
            {getActiveFiltersCount() > 0 && (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={clearAllFilters}
                  className="inline-flex items-center px-3 py-1 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <X className="h-4 w-4 mr-1" />
                  {t('consultant.services.filters.clear', 'Clear all filters')}
                </button>
              </div>
            )}
          </motion.div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Limit Reached Alert - Only show after clicking the button */}
      {showLimitMessage && canCreate && !canCreate.canCreate && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-start">
            <AlertCircle className="h-6 w-6 text-yellow-600 mt-0.5 mr-4 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('consultant.services.limit.title')}
              </h3>
              <p className="text-gray-600 mb-4">
                {canCreate.message || t('consultant.services.limit.message')}
              </p>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-blue-900">{t('consultant.services.limit.freePlan.title')}</h4>
                    <p className="text-blue-700 text-sm">{t('consultant.services.limit.freePlan.description')}</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={() => navigate('/consultant/subscriptions')}
                  className="bg-brand-primary text-white px-6 py-3 rounded-lg hover:opacity-90 transition-colors flex items-center justify-center space-x-2"
                >
                  <Crown className="h-5 w-5" />
                  <span>{t('consultant.services.limit.viewPlans')}</span>
                </button>
                <button
                  onClick={() => setShowLimitMessage(false)}
                  className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  {t('consultant.services.limit.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Services Grid */}
      {filteredServices.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-16"
        >
          <div className="w-24 h-24 bg-gradient-to-br from-brand-primary/10 to-brand-secondary/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Briefcase className="h-12 w-12 text-brand-primary" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">
            {services.length === 0 ? t('consultant.services.empty.noneFound', 'No services found') : t('consultant.services.empty.noneMatch', 'No services match your filters')}
          </h3>
          <p className="text-gray-600 mb-8 text-lg">
            {services.length === 0 
              ? t('consultant.services.empty.cta', "You don't have any services yet. Create your first one!")
              : t('consultant.services.empty.try', 'Try adjusting your search filters')
            }
          </p>
          {services.length === 0 && (
            <button 
              onClick={() => navigate('/consultant/services/create')}
              className="bg-gradient-to-r from-brand-primary to-brand-secondary text-white px-8 py-4 rounded-xl font-semibold hover:from-brand-primary/90 hover:to-brand-secondary/90 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <Plus className="h-5 w-5 mr-2 inline" />
              {t('consultant.services.empty.createFirst', 'Create my first service')}
            </button>
          )}
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {filteredServices.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden hover:shadow-lg transition-all duration-300 group"
            >
              {/* Image */}
              <div className="h-40 bg-gray-200 relative overflow-hidden">
                {service.banner_image ? (
                  <img
                    src={`${API_ENDPOINTS.UPLOADS}/${service.banner_image}`}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      e.currentTarget.src = 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=250&fit=crop';
                    }}
                    onLoad={() => {}}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-brand-primary/10 to-brand-secondary/10">
                    <Briefcase className="h-10 w-10 text-brand-primary" />
                  </div>
                )}
                
                {/* Status Badge */}
                <div className="absolute top-2 left-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(service.status)} border border-white/20 backdrop-blur-sm`}>
                    {getStatusLabel(service.status)}
                  </span>
                </div>

                {/* Approval Status Badge */}
                <div className="absolute top-2 right-2">
                  <ApprovalStatusBadge 
                    status={service.status}
                    size="sm"
                  />
                </div>
              </div>

              {/* Content */}
              <div className="p-4">
                <div className="mb-3">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-brand-primary transition-colors duration-200 line-clamp-2">
                    {service.title}
                  </h3>
                  
                  {/* Domain */}
                  {service.domaine && (
                    <div className="flex items-center gap-1 mb-2">
                      <Tag className="h-3 w-3 text-brand-secondary" />
                      <span className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded-full">
                        {service.domaine.name_french}
                      </span>
                    </div>
                  )}
                </div>

                {/* Rejection Reason */}
                {service.status === 'refused' && service.rejection_reason && (
                  <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-xs text-red-800">
                      <strong>Motif de refus :</strong> {service.rejection_reason}
                    </p>
                  </div>
                )}

                {/* Description */}
                <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                  {service.full_description}
                </p>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="text-center p-2 bg-gradient-to-br from-brand-primary/5 to-brand-primary/10 rounded-lg border border-brand-primary/20">
                    <div className="text-sm font-bold text-brand-primary">{service.format || 'N/A'}</div>
                    <div className="text-xs text-gray-600">{t('consultant.services.card.format', 'Format')}</div>
                  </div>
                  <div className="text-center p-2 bg-gradient-to-br from-brand-secondary/5 to-brand-secondary/10 rounded-lg border border-brand-secondary/20">
                    <div className="text-sm font-bold text-brand-secondary">{service.views || 0}</div>
                    <div className="text-xs text-gray-600">{t('consultant.services.card.views', 'Views')}</div>
                  </div>
                </div>

                {/* Location and Date */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <div className="w-5 h-5 bg-brand-mint/20 rounded flex items-center justify-center">
                      <Calendar className="h-3 w-3 text-brand-mint" />
                    </div>
                    <span className="truncate">{(() => {
                      // Get country from training_center or service
                      const country = service.training_center?.country?.name || 
                                    (typeof service.country === 'string' ? service.country : service.country?.name);
                      // City column contains both wilaya and city information
                      const city = service.city || service.training_center?.city;
                      
                      if (!country) return t('consultant.services.location.unknown', 'Location not specified');
                      
                      // Show country + city (which contains wilaya for Algeria)
                      return city ? `${city}, ${country}` : country;
                    })()}</span>
                  </div>
                
                </div>

                {/* Actions */}
                <div className="space-y-3">
                  {/* Primary Action - Disponibilité */}
                  <button 
                    onClick={() => navigate(`/consultant/services/${service.id}/creneaux`)}
                    disabled={service.status !== 'approved'}
                    className={`w-full py-3 px-4 rounded-xl font-semibold transition-all duration-200 text-sm ${
                      service.status === 'approved' 
                        ? 'bg-gradient-to-r from-brand-mint to-teal-600 text-white hover:from-teal-600 hover:to-brand-mint shadow-lg hover:shadow-xl transform hover:-translate-y-0.5' 
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                    title={service.status !== 'approved' ? t('consultant.services.actions.availability.tooltipDisabled', 'Cannot add availability for pending or rejected services') : t('consultant.services.actions.availability.tooltip', 'Manage availabilities')}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <UserPlus className="h-4 w-4" />
                      {service.status === 'approved' ? t('consultant.services.actions.availability.add', 'Add availability') : t('consultant.services.actions.availability.unavailable', 'Availability unavailable')}
                    </div>
                  </button>

                  {/* Secondary Actions */}
                  <div className="grid grid-cols-3 gap-2">
                    <button 
                      onClick={() => navigate(`/consultant/services/${service.id}/edit`)}
                      disabled={service.status !== 'approved'}
                      className={`p-3 border rounded-lg transition-all duration-200 ${
                        service.status === 'approved' 
                          ? 'border-brand-secondary text-brand-secondary hover:bg-brand-secondary hover:text-white' 
                          : 'border-gray-300 text-gray-400 cursor-not-allowed'
                      }`}
                      title={service.status !== 'approved' ? t('consultant.services.actions.edit.disabled', 'Edit not allowed') : t('consultant.services.actions.edit.tooltip', 'Edit service')}
                    >
                      <Edit className="h-4 w-4 mx-auto" />
                    </button>
                    <button 
                      onClick={() => navigate(`/consultant/services/${service.id}`)}
                      className="p-3 border border-brand-mint text-brand-mint rounded-lg hover:bg-brand-mint hover:text-white transition-all duration-200"
                      title={t('consultant.services.actions.view.tooltip', 'View details')}
                    >
                      <Eye className="h-4 w-4 mx-auto" />
                    </button>
                    <button 
                      onClick={() => handleDeleteService(service.id)}
                      className="p-3 border border-red-300 text-red-500 rounded-lg hover:bg-red-500 hover:text-white transition-all duration-200"
                      title={t('consultant.services.actions.delete.tooltip', 'Delete service')}
                    >
                      <Trash2 className="h-4 w-4 mx-auto" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ServicesPage;
